import React, { useEffect, useRef, useState } from 'react';
import { useSpinradContext } from '../../context/SpinradContext';
import { Note as NoteType, NoteColor } from '../../types';
import {
  CnDel,
  CnResizeHandle,
  CnResizeHandleHorizontal,
  CnText,
  CnToolbar,
  CnToolbarBtn,
  CnToolbarSwatch,
  Note as NoteBox,
} from './styles';

const colorOrder: NoteColor[] = ['default', 'white', 'darkgray'];

const maxWidth = 480;
const minWidth = 130;
const maxHeight = 420;
const minHeight = 60;

const minFontSize = 20;
const maxFontSize = 100;

interface ChalkNoteProps {
  cardId: number;
  note: NoteType;
  color: string;
  gridRef: React.RefObject<HTMLDivElement>;
  boardRef: React.RefObject<HTMLDivElement>;
}

interface Pos {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

const ChalkNote: React.FC<ChalkNoteProps> = ({
  cardId,
  note,
  color,
  gridRef,
  boardRef,
}) => {
  const {
    updateNote,
    deleteNote,
    confirmDeleteNoteFlow,
    setNotePosition,
    resizeNote,
    setNoteColor,
    bumpNoteZ,
  } = useSpinradContext();
  const elRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragPos, setDragPos] = useState<Pos | null>(null);
  const [resizing, setResizing] = useState(false);
  const [sizePx, setSizePx] = useState<Size | null>(null);
  const [editing, setEditing] = useState(false);
  const [fmt, setFmt] = useState({ b: false, i: false, u: false });
  const dragOffset = useRef<Pos>({ x: 0, y: 0 });
  const posRef = useRef<Pos>({ x: note.x, y: note.y });
  const resizeStart = useRef<{ x: number; y: number } & Size>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const sizeRef = useRef<Size | null>(null);
  const resizeAxis = useRef<'both' | 'horizontal'>('both');

  const syncFmt = (): void => {
    try {
      setFmt({
        b: document.queryCommandState('bold'),
        i: document.queryCommandState('italic'),
        u: document.queryCommandState('underline'),
      });
    } catch {
      // queryCommandState unsupported - ignore
    }
  };

  useEffect(() => {
    if (!editing) return undefined;
    document.addEventListener('selectionchange', syncFmt);
    return () => document.removeEventListener('selectionchange', syncFmt);
  }, [editing]);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMouseMove = (e: MouseEvent): void => {
      const grid = gridRef.current;
      if (!grid) return;
      const gr = grid.getBoundingClientRect();
      const x = Math.max(0, e.clientX - gr.left - dragOffset.current.x);
      const y = Math.max(0, e.clientY - gr.top - dragOffset.current.y);
      posRef.current = { x, y };
      setDragPos({ x, y });

      const board = boardRef.current;
      if (board) {
        const br = board.getBoundingClientRect();
        if (e.clientY > br.bottom - 50) board.scrollTop += 8;
        if (e.clientY < br.top + 70) board.scrollTop -= 8;
      }
    };

    const onMouseUp = (): void => {
      setDragging(false);
      setDragPos(null);
      setNotePosition(cardId, note.id, posRef.current.x, posRef.current.y);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging, cardId, note.id, gridRef, boardRef, setNotePosition]);

  useEffect(() => {
    if (!resizing) return undefined;

    const onMouseMove = (e: MouseEvent): void => {
      if (resizeAxis.current === 'horizontal') {
        const width = Math.min(
          maxWidth,
          Math.max(
            minWidth,
            resizeStart.current.width + (e.clientX - resizeStart.current.x),
          ),
        );
        sizeRef.current = { width, height: resizeStart.current.height };
        setSizePx({ width, height: resizeStart.current.height });
        return;
      }

      const delta =
        (e.clientX -
          resizeStart.current.x +
          (e.clientY - resizeStart.current.y)) /
        2;
      const width = Math.min(
        maxWidth,
        Math.max(minWidth, resizeStart.current.width + delta),
      );
      const height = Math.min(
        maxHeight,
        Math.max(minHeight, resizeStart.current.height + delta),
      );
      sizeRef.current = { width, height };
      setSizePx({ width, height });
    };

    const onMouseUp = (): void => {
      setResizing(false);
      if (sizeRef.current) {
        resizeNote(
          cardId,
          note.id,
          sizeRef.current.width,
          sizeRef.current.height,
        );
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizing, cardId, note.id, resizeNote]);

  const startDrag = (e: React.MouseEvent): void => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-drag]')) return;
    const el = elRef.current;
    if (!el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    posRef.current = { x: note.x, y: note.y };
    bumpNoteZ(cardId, note.id);
    setDragging(true);
  };

  const beginResize =
    (axis: 'both' | 'horizontal') =>
    (e: React.MouseEvent): void => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const el = elRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        width: note.width ?? r.width,
        height: note.height ?? r.height,
      };
      resizeAxis.current = axis;
      sizeRef.current = null;
      bumpNoteZ(cardId, note.id);
      setResizing(true);
    };

  const handleBlur = (): void => {
    setTimeout(() => setEditing(false), 120);
    const el = textRef.current;
    if (!el) return;
    const plain = (el.innerText || '').trim();
    if (!plain) {
      deleteNote(cardId, note.id);
      return;
    }
    updateNote(cardId, note.id, plain, el.innerHTML);
  };

  const runCmd =
    (cmd: 'bold' | 'italic' | 'underline') =>
    (e: React.MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      document.execCommand(cmd, false);
      textRef.current?.focus();
      syncFmt();
    };

  const cycleColor = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const next =
      colorOrder[(colorOrder.indexOf(note.color) + 1) % colorOrder.length];
    setNoteColor(cardId, note.id, next);
  };

  const left = dragging && dragPos ? dragPos.x : note.x;
  const top = dragging && dragPos ? dragPos.y : note.y;
  const width = resizing && sizePx ? sizePx.width : note.width;
  const height = resizing && sizePx ? sizePx.height : note.height;
  const fontSize =
    width && height
      ? (() => {
          const widthProgress = (width - minWidth) / (maxWidth - minWidth);
          const heightProgress = (height - minHeight) / (maxHeight - minHeight);
          const progress = Math.min(widthProgress, heightProgress);
          return minFontSize + progress * (maxFontSize - minFontSize);
        })()
      : undefined;
  const resolvedColor =
    note.color === 'white'
      ? '#ffffff'
      : note.color === 'darkgray'
        ? '#a9a9a9'
        : color;

  return (
    <NoteBox
      ref={elRef}
      $dragging={dragging}
      $color={resolvedColor}
      data-note-id={note.id}
      style={{
        left,
        top,
        transform: `rotate(${note.rot}deg)`,
        zIndex: note.z || 1,
        ...(width ? { width, maxWidth: 'none' } : {}),
        ...(height ? { minHeight: height } : {}),
      }}
      onMouseDown={startDrag}
    >
      <CnToolbar $visible={editing} data-no-drag>
        <CnToolbarBtn
          type="button"
          $variant="b"
          $active={fmt.b}
          onMouseDown={runCmd('bold')}
          title="Fett"
        >
          B
        </CnToolbarBtn>
        <CnToolbarBtn
          type="button"
          $variant="i"
          $active={fmt.i}
          onMouseDown={runCmd('italic')}
          title="Kursiv"
        >
          I
        </CnToolbarBtn>
        <CnToolbarBtn
          type="button"
          $variant="u"
          $active={fmt.u}
          onMouseDown={runCmd('underline')}
          title="Unterstrichen"
        >
          U
        </CnToolbarBtn>
        <CnToolbarBtn
          type="button"
          $variant="c"
          onMouseDown={cycleColor}
          title="Textfarbe (Standard / Weiß / Dunkelgrau)"
        >
          <CnToolbarSwatch $swatch={resolvedColor} />
        </CnToolbarBtn>
      </CnToolbar>
      <CnDel
        type="button"
        data-no-drag
        title="Entfernen"
        onMouseDown={e => e.stopPropagation()}
        onClick={e => {
          e.stopPropagation();
          confirmDeleteNoteFlow(cardId, note.id);
        }}
      >
        ✕
      </CnDel>
      <CnText
        ref={textRef}
        data-no-drag
        data-cn-text
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        style={fontSize ? { fontSize } : undefined}
        dangerouslySetInnerHTML={{ __html: note.html || note.text }}
        onMouseDown={e => e.stopPropagation()}
        onFocus={() => {
          setEditing(true);
          syncFmt();
        }}
        onBlur={handleBlur}
        onKeyUp={syncFmt}
        onMouseUp={syncFmt}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            e.preventDefault();
            textRef.current?.blur();
          }
        }}
      />
      <CnResizeHandleHorizontal
        data-no-drag
        onMouseDown={beginResize('horizontal')}
        title="Breite ändern"
      />
      <CnResizeHandle
        data-no-drag
        onMouseDown={beginResize('both')}
        title="Größe ändern"
      >
        ⤡
      </CnResizeHandle>
    </NoteBox>
  );
};

export default ChalkNote;
